import { outcomeOf, type RunPlan, type RunStatus } from "@wayfind/core";
import { outcomeWords, reasonWords } from "../words";
import type { StopReasonCode } from "@wayfind/core";

/**
 * The run as a single vertical thread.
 *
 * This is the same object on a live run and a stops-only one, because it
 * is the same fact: a sequence of places, some of which have been dealt
 * with. Nothing here depends on a position, which is why a coarse run
 * loses only the map and not the screen.
 */
export function StopThread({
  plan,
  status,
  myStopIndex,
  coarse = false
}: {
  plan: RunPlan;
  status: RunStatus;
  myStopIndex: number | null;
  /** A stops-only run calls at stops; it does not deliver to them. */
  coarse?: boolean;
}): JSX.Element {
  return (
    <ol className="thread">
      {plan.stops.map(stop => {
        const outcome = outcomeOf(status.outcomes, stop.index);
        const mine = stop.index === myStopIndex;
        const dealtWith = stop.index <= status.stopIndex;
        const current = !mine
          && outcome === "pending"
          && stop.index === status.stopIndex + 1;
        const reason = status.lastOutcome?.stopIndex === stop.index
          ? reasonWords(status.lastOutcome.reasonCode as StopReasonCode)
          : null;

        const classes = [
          "stop",
          outcome === "delivered" ? "stop--done" : "",
          outcome === "skipped" || outcome === "failed" ? "stop--halt" : "",
          mine ? "stop--mine" : "",
          current ? "stop--current" : ""
        ].filter(Boolean).join(" ");

        return (
          <li
            key={stop.index}
            className={classes}
            // Screen readers get the whole stop as one sentence rather
            // than three fragments read in layout order.
            aria-label={[
              `Stop ${stop.index}`,
              stop.label ?? null,
              mine ? "your stop" : null,
              outcome === "pending" ? (dealtWith ? "passed" : "waiting") : outcomeWords(outcome, coarse).toLowerCase(),
              reason
            ].filter(Boolean).join(", ")}
          >
            <div className="stop__rail" aria-hidden="true">
              <span className="stop__node" />
            </div>
            <div>
              <p className="stop__label">
                {stop.label ?? `Stop ${stop.index}`}
                {mine ? <span className="stop__you">yours</span> : null}
              </p>
              {reason ? <p className="stop__note">{reason}</p> : null}
            </div>
            <div className="stop__aside">
              {outcome === "pending"
                // "Pending" on the wire means *nobody has said* — never
                // "the vehicle has not arrived". So an unmarked stop the
                // van has already driven past says exactly that.
                ? (dealtWith ? "passed" : "")
                : outcomeWords(outcome, coarse)}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The stops-only counter: what a school-transport parent gets instead of
 * a vehicle. Nothing in it is derived from a position, because there is
 * none — and it is deliberately a different object from the live view
 * rather than the live view with a piece missing.
 */
export function StopCounter({
  plan,
  status
}: {
  plan: RunPlan | null;
  status: RunStatus;
}): JSX.Element {
  const total = plan?.stops.length ?? status.outcomes.length;
  const cleared = Math.min(status.stopIndex, total);
  return (
    <div>
      <p className="eyebrow">Stops cleared</p>
      <p className="counter">
        <span className="counter__big">{cleared}</span>
        <span className="counter__of">of {total}</span>
      </p>
      <div className="counter__bar" role="img" aria-label={`${cleared} of ${total} stops cleared`}>
        {Array.from({ length: total }, (_, index) => {
          const outcome = outcomeOf(status.outcomes, index + 1);
          const cellClass = outcome === "delivered"
            ? "counter__cell counter__cell--done"
            : outcome === "skipped" || outcome === "failed"
              ? "counter__cell counter__cell--halt"
              : index + 1 === status.stopIndex + 1
                ? "counter__cell counter__cell--next"
                : "counter__cell";
          return <span key={index} className={cellClass} />;
        })}
      </div>
    </div>
  );
}
